import i18n from '../../i18n/config';
import { useTranslation } from 'react-i18next';
// VehicleOperatorsPanel.jsx
//
// Panel reutilizable que gestiona los operadores asignados a UN vehículo.
// Se usa en:
//   - Popover/diálogo del botón del panel de control (VehicleOperatorsButton).
//   - Tab "Operadores" del detalle del vehículo.
//
// El panel tiene TRES vistas internas (lista / buscador / alta rápida) en lugar
// de abrir menús y modales anidados: un menú anclado dentro de un popover se
// descolocaba al reposicionarse el contenedor. Al ser vistas internas, todo
// queda siempre dentro del mismo contenedor y no puede "irse hacia arriba".

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  IconButton,
  InputAdornment,
  List,
  ListItem,
  ListItemAvatar,
  ListItemButton,
  ListItemText,
  Stack,
  TextField,
  Tooltip,
  Typography,
  alpha,
  useTheme,
} from '@mui/material';
import {
  PersonAdd as PersonAddIcon,
  PersonRemove as PersonRemoveIcon,
  SwapHoriz as SwapIcon,
  Close as CloseIcon,
  Search as SearchIcon,
  ArrowBack as BackIcon,
  Add as AddIcon,
} from '@mui/icons-material';

import { useAuth } from '../../hooks/useAuth';
import { useNotification } from '../common/GlobalNotifications';
import { api } from '../../services/api';
import OperatorContactMenu from './OperatorContactMenu';
import OperatorQuickCreateForm from './OperatorQuickCreateForm';

const VIEW_LIST = i18n.t('hardcoded.components_operators_vehicleoperatorspanel.list');
const VIEW_PICK = i18n.t('hardcoded.components_operators_vehicleoperatorspanel.pick');
const VIEW_CREATE = i18n.t('hardcoded.components_auth_createusermanually.create');

const VehicleOperatorsPanel = ({
  vehicleId,
  canEdit: canEditProp,
  compact = false,        // true → versión densa para popover
  onAssignmentsChanged,    // callback opcional cuando cambian las asignaciones
  onClose,                 // si se pasa, agrega botón cerrar en el header
  maxOperators,            // límite del vehículo. Si no se pasa, lo dice el backend.
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const { hasPermission } = useAuth();
  const { showSuccess, showError } = useNotification();

  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState(VIEW_LIST);
  const [targetSlot, setTargetSlot] = useState(null);
  const [busy, setBusy] = useState(null);       // acción en curso: 'assign' | 'remove:<id>' | 'create'
  const [formError, setFormError] = useState(null);
  const [confirmRemove, setConfirmRemove] = useState(null);
  const [moveConfirm, setMoveConfirm] = useState(null);

  // Buscador de operadores existentes.
  const [search, setSearch] = useState('');
  const [debounced, setDebounced] = useState('');
  const [candidates, setCandidates] = useState([]);
  const [searching, setSearching] = useState(false);

  // El backend manda: `can_edit` viene del rol real. La prop solo puede restringir.
  const canEdit = (summary?.can_edit ?? (canEditProp !== undefined ? canEditProp : hasPermission('manager')))
    && (canEditProp === undefined || canEditProp);

  const effectiveMax = Number(maxOperators ?? summary?.max_operators ?? 1) || 1;
  const assignments = useMemo(() => summary?.operators || [], [summary]);
  const totalAssigned = assignments.length;

  // El padre suele pasar `onAssignmentsChanged` como funcion inline, asi que su
  // identidad cambia en CADA render suyo. Si entrara en las dependencias de
  // `load`, el efecto se volveria a disparar sin fin:
  //   load() -> onAssignmentsChanged() -> setState del padre -> nueva funcion
  //   -> nuevo `load` -> efecto -> load()...
  // Eso provocaba una tormenta de peticiones y dejaba el panel en blanco.
  // Guardarlo en un ref mantiene `load` estable sin importar como lo llamen.
  const onAssignmentsChangedRef = useRef(onAssignmentsChanged);
  useEffect(() => { onAssignmentsChangedRef.current = onAssignmentsChanged; }, [onAssignmentsChanged]);

  const load = useCallback(async () => {
    if (!vehicleId) return;
    setLoading(true);
    try {
      const r = await api.operators.vehicleSummary(vehicleId);
      setSummary(r.data);
      onAssignmentsChangedRef.current?.(r.data?.operators || []);
    } catch (e) {
      showError(t('hardcoded.components_operators_vehicleoperatorspanel.no_se_pudieron_cargar_los'));
    } finally {
      setLoading(false);
    }
    // Solo el vehiculo (y el notificador, ya memoizado) deben re-disparar la carga.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vehicleId, showError]);

  useEffect(() => { load(); }, [load]);

  // Debounce del buscador.
  useEffect(() => {
    const t = setTimeout(() => setDebounced(search.trim()), 300);
    return () => clearTimeout(t);
  }, [search]);

  useEffect(() => {
    if (view !== VIEW_PICK) return undefined;
    let cancelled = false;
    setSearching(true);
    api.operators.list({ active_only: true, search: debounced || undefined, limit: 50 })
      .then((r) => { if (!cancelled) setCandidates(r.data || []); })
      .catch(() => { if (!cancelled) setCandidates([]); })
      .finally(() => { if (!cancelled) setSearching(false); });
    return () => { cancelled = true; };
  }, [view, debounced]);

  const backToList = () => {
    setView(VIEW_LIST);
    setTargetSlot(null);
    setSearch('');
    setFormError(null);
    setConfirmRemove(null);
  };

  const openPicker = (slot) => { setTargetSlot(slot); setView(VIEW_PICK); };
  const openCreate = (slot) => { setTargetSlot(slot); setFormError(null); setView(VIEW_CREATE); };

  // Un operador no puede conducir dos unidades. Si el backend responde 409 con
  // el codigo correspondiente, se pide confirmacion explicita para MOVERLO.
  const handleAssign = async (operatorId, { force = false } = {}) => {
    setBusy('assign');
    try {
      await api.operators.assignToVehicle(vehicleId, operatorId, targetSlot || null, { force });
      showSuccess(force
        ? t('hardcoded.components_operators_vehicleoperatorspanel.operador_movido')
        : t('hardcoded.components_operators_operatorvehiclespanel.operador_asignado_al_vehiculo'));
      setMoveConfirm(null);
      backToList();
      await load();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (e?.response?.status === 409 && detail && detail.code === 'operator_already_assigned') {
        setMoveConfirm({ operatorId, message: detail.message, currentVehicle: detail.current_vehicle_name });
        return;
      }
      showError((typeof detail === 'string' ? detail : detail?.message)
        || t('hardcoded.components_operators_vehicleoperatorspanel.no_se_pudo_asignar_el'));
    } finally {
      setBusy(null);
    }
  };

  // "Crear" da de alta al operador SIN ocupar el puesto de esta unidad.
  const handleCreateOnly = async (payload) => {
    setBusy('create');
    setFormError(null);
    try {
      await api.operators.create(payload);
      showSuccess(t('hardcoded.components_operators_vehicleoperatorspanel.operador_creado'));
      backToList();
      await load();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      setFormError((typeof detail === 'string' ? detail : detail?.message)
        || t('hardcoded.components_operators_vehicleoperatorspanel.no_se_pudo_crear_el'));
    } finally {
      setBusy(null);
    }
  };

  const handleCreateAndAssign = async (payload) => {
    setBusy(t('hardcoded.components_auth_createusermanually.create'));
    setFormError(null);
    try {
      await api.operators.createAndAssign(vehicleId, { ...payload, slot: targetSlot || null });
      showSuccess(t('hardcoded.components_operators_vehicleoperatorspanel.operador_creado_y_asignado_correctamente'));
      backToList();
      await load();
    } catch (e) {
      setFormError(e?.response?.data?.detail || t('hardcoded.components_operators_vehicleoperatorspanel.no_se_pudo_crear_el'));
    } finally {
      setBusy(null);
    }
  };

  const handleUnassign = async (operatorId) => {
    setBusy(`remove:${operatorId}`);
    try {
      await api.operators.unassignFromVehicle(vehicleId, operatorId);
      showSuccess(t('hardcoded.components_operators_vehicleoperatorspanel.operador_quitado_del_vehiculo'));
      setConfirmRemove(null);
      await load();
    } catch (e) {
      showError(e?.response?.data?.detail || t('hardcoded.components_operators_vehicleoperatorspanel.no_se_pudo_quitar_el'));
    } finally {
      setBusy(null);
    }
  };

  // Posiciones a mostrar: las del cupo del vehículo + las ocupadas por datos
  // previos (p. ej. legado con 2 operadores en un vehículo hoy de 1).
  const rows = useMemo(() => {
    const bySlot = new Map(assignments.map((a) => [String(a.slot), a]));
    const slots = new Set([
      ...Array.from({ length: effectiveMax }, (_, i) => String(i + 1)),
      ...assignments.map((a) => String(a.slot)),
    ]);
    return [...slots].sort().map((slot) => ({ slot, assignment: bySlot.get(slot) || null }));
  }, [assignments, effectiveMax]);

  const roleLabelFor = (slot) => {
    if (effectiveMax <= 1) return t('hardcoded.components_operators_vehicleoperatorspanel.operador');
    return slot === '1' ? t('hardcoded.components_common_imageupload.principal') : t('hardcoded.components_operators_vehicleoperatorspanel.secundario');
  };

  const header = (
    <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: compact ? 1 : 2 }}>
      <Typography variant={compact ? 'subtitle2' : 'subtitle1'} fontWeight={700}>
        {t('hardcoded.components_operators_vehicleoperatorspanel.operadores_asignados')}<Chip
          size="small"
          sx={{ ml: 1, height: 20 }}
          color={totalAssigned > effectiveMax ? 'error' : 'default'}
          label={`${totalAssigned}/${effectiveMax}`}
        />
      </Typography>
      {onClose && (
        <Tooltip title={t('common.close')}>
          <IconButton size="small" onClick={onClose}><CloseIcon fontSize="small" /></IconButton>
        </Tooltip>
      )}
    </Stack>
  );

  // ---------- Vista: alta rápida ----------
  if (view === VIEW_CREATE) {
    return (
      <Box>
        {header}
        <OperatorQuickCreateForm
          roleLabel={effectiveMax > 1 ? roleLabelFor(targetSlot) : null}
          onCancel={backToList}
          onSubmit={handleCreateAndAssign}
          onCreateOnly={handleCreateOnly}
          saving={busy === 'create'}
          error={formError}
        />
      </Box>
    );
  }

  // ---------- Vista: buscador de operadores existentes ----------
  if (view === VIEW_PICK) {
    const assignedIds = new Set(assignments.map((a) => String(a.operator.id)));
    const available = candidates.filter((o) => !assignedIds.has(String(o.id)));
    return (
      <Box>
        {header}
        <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mb: 1 }}>
          <Button size="small" startIcon={<BackIcon />} onClick={backToList} sx={{ textTransform: 'none' }}>
            {i18n.t('hardcoded.components_operators_operatorquickcreateform.volver')}</Button>
          <Typography variant="subtitle2" fontWeight={700}>
            {i18n.t('common.assign')}{effectiveMax > 1 ? roleLabelFor(targetSlot).toLowerCase() : t('hardcoded.components_operators_vehicleoperatorspanel.operador')}

          {/* Un operador solo puede conducir una unidad: se confirma el traslado. */}
          {moveConfirm && (
            <Alert
              severity="warning"
              sx={{ mb: 1, py: 0.25 }}
              action={
                <Stack direction="row" spacing={0.5}>
                  <Button size="small" onClick={() => setMoveConfirm(null)} sx={{ textTransform: 'none' }}>
                    {t('common.cancel')}
                  </Button>
                  <Button size="small" color="warning" variant="contained" disabled={Boolean(busy)}
                    onClick={() => handleAssign(moveConfirm.operatorId, { force: true })}
                    sx={{ textTransform: 'none' }}>
                    {t('hardcoded.components_operators_vehicleoperatorspanel.mover_operador')}
                  </Button>
                </Stack>
              }
            >
              <Typography variant="caption">{moveConfirm.message}</Typography>
            </Alert>
          )}
          </Typography>
        </Stack>

        <TextField
          size="small" fullWidth autoFocus placeholder={t('hardcoded.components_operators_vehicleoperatorspanel.buscar_por_nombre_documento_telefono')}
          value={search} onChange={(e) => setSearch(e.target.value)}
          onKeyDown={(e) => e.stopPropagation()}
          InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
          sx={{ mb: 1 }}
        />

        <Box sx={{ maxHeight: 260, overflowY: 'auto' }}>
          {searching ? (
            <Box sx={{ py: 3, textAlign: 'center' }}><CircularProgress size={22} /></Box>
          ) : available.length === 0 ? (
            <Box sx={{ py: 2, textAlign: 'center' }}>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                {debounced ? t('hardcoded.components_operators_vehicleoperatorspanel.ningun_operador_coincide_con_la') : t('hardcoded.components_operators_vehicleoperatorspanel.no_hay_operadores_disponibles_para')}
              </Typography>
              <Button size="small" startIcon={<AddIcon />} onClick={() => openCreate(targetSlot)} sx={{ textTransform: 'none' }}>
                {t('hardcoded.components_operators_vehicleoperatorspanel.crear_operador')}</Button>
            </Box>
          ) : (
            <List dense disablePadding>
              {available.map((op) => (
                <ListItemButton
                  key={op.id}
                  disabled={busy === 'assign'}
                  onClick={() => handleAssign(op.id)}
                  sx={{ borderRadius: 1, mb: 0.5 }}
                >
                  <ListItemAvatar sx={{ minWidth: 40 }}>
                    <Avatar sx={{ width: 30, height: 30, fontSize: '0.85rem' }}>
                      {op.full_name?.[0] || '?'}
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    primary={<Typography variant="body2" fontWeight={600}>{op.full_name}</Typography>}
                    secondary={
                      <Typography variant="caption" color="text.secondary">
                        {[op.phone, op.license_number && t('hardcoded.components_operators_vehicleoperatorspanel.lic_license_number', { license_number: op.license_number })]
                          .filter(Boolean).join(' · ') || t('hardcoded.components_operators_vehicleoperatorspanel.sin_datos_de_contacto')}
                      </Typography>
                    }
                  />
                  {op.assigned_vehicle_count > 0 && (
                    <Chip size="small" label={`${op.assigned_vehicle_count} veh.`} sx={{ height: 18, fontSize: 10 }} />
                  )}
                </ListItemButton>
              ))}
            </List>
          )}
        </Box>

        {available.length > 0 && (
          <>
            <Divider sx={{ my: 1 }} />
            <Button fullWidth size="small" startIcon={<AddIcon />} onClick={() => openCreate(targetSlot)}
              sx={{ textTransform: 'none' }}>
              {t('hardcoded.components_operators_vehicleoperatorspanel.crear_operador_nuevo')}</Button>
          </>
        )}
      </Box>
    );
  }

  // ---------- Vista: lista de posiciones ----------
  return (
    <Box>
      {header}

      {!compact && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
          {effectiveMax === 1
            ? t('hardcoded.components_operators_vehicleoperatorspanel.este_vehiculo_lleva_un_solo')
            : t('hardcoded.components_operators_vehicleoperatorspanel.este_vehiculo_puede_llevar_hasta', { effectiveMax: effectiveMax })}
          {t('hardcoded.components_operators_vehicleoperatorspanel.solo_se_pueden_asignar_operadores')}</Typography>
      )}

      {totalAssigned > effectiveMax && (
        <Alert severity="warning" sx={{ mb: 1 }}>
          {t('hardcoded.components_operators_vehicleoperatorspanel.este_vehiculo_esta_configurado_para')}{effectiveMax} {t('hardcoded.components_operators_vehicleoperatorspanel.operador')}{effectiveMax === 1 ? '' : 'es'}{t('hardcoded.components_operators_vehicleoperatorspanel.pero_tiene')}{totalAssigned}. Quita {totalAssigned - effectiveMax} {t('hardcoded.components_operators_vehicleoperatorspanel.para_dejarlo_consistente')}</Alert>
      )}

      {loading ? (
        <Box sx={{ py: 4, textAlign: 'center' }}><CircularProgress size={24} /></Box>
      ) : (
        <List dense disablePadding>
          {rows.map(({ slot, assignment: a }) => {
            const canAddHere = canEdit && !a && totalAssigned < effectiveMax;
            const removing = busy === `remove:${a?.operator?.id}`;
            return (
              <ListItem
                key={slot}
                disableGutters
                sx={{
                  display: 'block',
                  px: 1.25,
                  py: 1,
                  mb: 0.75,
                  borderRadius: 1.5,
                  transition: 'background .2s ease',
                  background: a ? alpha(theme.palette.success.main, 0.06) : alpha(theme.palette.grey[500], 0.04),
                  border: '1px solid',
                  borderColor: a ? alpha(theme.palette.success.main, 0.2) : 'divider',
                }}
              >
                <Stack direction="row" alignItems="flex-start" spacing={1.25}>
                  <Avatar sx={{
                    width: compact ? 34 : 40, height: compact ? 34 : 40,
                    bgcolor: a ? 'primary.main' : 'grey.400', fontSize: '0.95rem',
                  }}>
                    {a?.operator?.full_name?.[0] || <PersonAddIcon fontSize="small" />}
                  </Avatar>

                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    {a ? (
                      <>
                        <Stack direction="row" alignItems="center" spacing={0.75} flexWrap="wrap">
                          <Typography variant="body2" fontWeight={700} noWrap>
                            {a.operator.full_name}
                          </Typography>
                          {effectiveMax > 1 && (
                            <Chip size="small" label={a.role_label || roleLabelFor(slot)}
                              sx={{ height: 18, fontSize: '0.65rem' }} />
                          )}
                          {!a.operator.is_active && (
                            <Chip size="small" color="warning" label={t('hardcoded.components_alarmprofiles_assignmentstable.inactivo')} sx={{ height: 18, fontSize: '0.65rem' }} />
                          )}
                        </Stack>
                        <Box sx={{ mt: 0.5 }}>
                          <OperatorContactMenu
                            phone={a.operator.phone}
                            phoneE164={a.phone_e164}
                            name={a.operator.full_name}
                          />
                        </Box>
                        {a.operator.license_number && (
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                            {[a.operator.license_number && t('hardcoded.components_operators_vehicleoperatorspanel.lic_license_number', { license_number: a.operator.license_number })]
                              .filter(Boolean).join(' · ')}
                          </Typography>
                        )}
                      </>
                    ) : (
                      <>
                        <Typography variant="body2" fontWeight={600} color="text.secondary">
                          {effectiveMax > 1 ? t('hardcoded.components_operators_vehicleoperatorspanel.operador_rolelabelforslo', { roleLabelForslo: roleLabelFor(slot).toLowerCase() }) : t('hardcoded.components_operators_vehicleoperatorspanel.sin_operador_asignado')}
                        </Typography>
                        <Typography variant="caption" color="text.disabled">
                          {canEdit ? 'Asigna uno existente o crea uno nuevo.' : t('hardcoded.components_operators_vehicleoperatorspanel.este_vehiculo_no_tiene_operador')}
                        </Typography>
                      </>
                    )}
                  </Box>

                  {canEdit && a && (
                    <Stack direction="row" spacing={0.25}>
                      <Tooltip title={t('hardcoded.components_operators_vehicleoperatorspanel.cambiar_operador')}>
                        <span>
                          <IconButton size="small" disabled={Boolean(busy)} onClick={() => openPicker(slot)}>
                            <SwapIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                      <Tooltip title={t('hardcoded.components_operators_vehicleoperatorspanel.quitar_operador')}>
                        <span>
                          <IconButton size="small" color="error" disabled={Boolean(busy)}
                            onClick={() => setConfirmRemove(a.operator.id)}>
                            {removing ? <CircularProgress size={16} /> : <PersonRemoveIcon fontSize="small" />}
                          </IconButton>
                        </span>
                      </Tooltip>
                    </Stack>
                  )}
                </Stack>

                {/* Confirmación en línea: no abre otro modal encima del panel. */}
                {confirmRemove === a?.operator?.id && (
                  <Alert
                    severity="warning"
                    sx={{ mt: 1, py: 0.25 }}
                    action={
                      <Stack direction="row" spacing={0.5}>
                        <Button size="small" onClick={() => setConfirmRemove(null)} sx={{ textTransform: 'none' }}>
                          {t('common.cancel')}</Button>
                        <Button size="small" color="error" variant="contained" disabled={Boolean(busy)}
                          onClick={() => handleUnassign(a.operator.id)} sx={{ textTransform: 'none' }}>
                          {i18n.t('hardcoded.components_operators_vehicleoperatorspanel.quitar')}</Button>
                      </Stack>
                    }
                  >
                    <Typography variant="caption">{t('hardcoded.components_operators_vehicleoperatorspanel.quitar_a')}{a.operator.full_name} {t('hardcoded.components_operators_vehicleoperatorspanel.del_vehiculo')}</Typography>
                  </Alert>
                )}

                {canAddHere && (
                  <Stack direction="row" spacing={0.75} sx={{ mt: 1 }}>
                    <Button size="small" variant="outlined" startIcon={<PersonAddIcon />}
                      onClick={() => openPicker(slot)} sx={{ textTransform: 'none', flex: 1 }}>
                      {i18n.t('common.assign')}</Button>
                    <Button size="small" variant="contained" startIcon={<AddIcon />}
                      onClick={() => openCreate(slot)} sx={{ textTransform: 'none', flex: 1 }}>
                      {t('audit.actions.CREATE')}</Button>
                  </Stack>
                )}
              </ListItem>
            );
          })}
        </List>
      )}

      {!canEdit && !loading && (
        <>
          <Divider sx={{ my: 1.5 }} />
          <Typography variant="caption" color="text.secondary">
            {t('hardcoded.components_operators_vehicleoperatorspanel.no_tienes_permisos_para_asignar')}</Typography>
        </>
      )}
    </Box>
  );
};

export default VehicleOperatorsPanel;
